import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import { LOCIZE_NAMESPACE } from 'main/common'
import Form, { FormGroup, FormSection } from 'main/components/common/Form'
import { IChild, UPSERT_CHILD_MUTATION, UpsertChild, UpsertChildVariables } from 'main/gql/child'
import { Gender } from 'main/gql/globalTypes'
import { Button, DateTextField, Select, TextField } from 'main/ui'
import { formatDateInput } from 'main/util/date'
import { TFunction, useTranslation } from 'react-i18next'
import { i18nKeyPrefix } from '../../../i18n'
import refetchQuery from './refetchQuery'
import { IChildParams, IChildValidation } from './types'
import { initialParams, validate } from './util'

const PRE = `${i18nKeyPrefix}-BASE-CHILDREN-SELECTOR-FORM`

interface IProps {
  child: IChild | null
  validationHints: IChildValidation
  onChangeValidationHints: (validationHints: IChildValidation) => void
  onComplete: (child: IChild) => void
}

export default function ChildForm(props: IProps) {
  const { child, validationHints, onChangeValidationHints, onComplete } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [params, setParams] = React.useState<IChildParams>(initialParams(child))

  return (
    <Mutation<UpsertChild, UpsertChildVariables>
      mutation={UPSERT_CHILD_MUTATION}
      onCompleted={data => {
        if (data.upsertChild) {
          onComplete(data.upsertChild)
        }
      }}
      refetchQueries={[{ query: refetchQuery }]}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(params, mutate, onChangeValidationHints)}>
          <FormSection widthConstraint={false}>
            <FormGroup>
              <TextField
                label={t(`${PRE}-FIRST-NAME-LABEL`)}
                placeholder={t(`${PRE}-FIRST-NAME-PLACEHOLDER`)}
                validationHint={validationHints.firstName}
                value={params.firstName}
                onChange={firstName => handleParamsChange({ firstName }, setParams)}
                required
              />
              <TextField
                label={t(`${PRE}-LAST-NAME-LABEL`)}
                placeholder={t(`${PRE}-LAST-NAME-PLACEHOLDER`)}
                validationHint={validationHints.lastName}
                value={params.lastName}
                onChange={lastName => handleParamsChange({ lastName }, setParams)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Select
                label={t(`${PRE}-GENDER-LABEL`)}
                invalid={!!validationHints.gender}
                value={params.gender}
                onChange={value => {
                  handleParamsChange({ gender: value ? (value as Gender) : null }, setParams)
                }}
                required
              >
                <option value="" />
                <option value="BOY">{t(`${PRE}-BOY`)}</option>
                <option value="GIRL">{t(`${PRE}-GIRL`)}</option>
                <option value="OTHER">{t(`${PRE}-OTHER`)}</option>
              </Select>
              <DateTextField
                label={t(`${PRE}-BIRTH-LABEL`)}
                placeholder="mm/dd/yyyy"
                validationHint={validationHints.birthday}
                value={params.birthday}
                onChange={birthday => handleParamsChange({ birthday }, setParams)}
                required
              />
            </FormGroup>

            <FormGroup>
              <DateTextField
                label={t(`${PRE}-START-LABEL`)}
                placeholder="mm/dd/yyyy"
                validationHint={validationHints.desiredEnrollmentDate}
                value={params.desiredEnrollmentDate}
                onChange={desiredEnrollmentDate =>
                  handleParamsChange({ desiredEnrollmentDate }, setParams)
                }
                required
              />
              <div>
                <Button disabled={loading} full>
                  {buttonCta(child, loading, t)}
                </Button>
              </div>
            </FormGroup>
          </FormSection>
        </Form>
      )}
    </Mutation>
  )
}

function handleParamsChange(
  changes: Partial<IChildParams>,
  setParams: React.Dispatch<React.SetStateAction<IChildParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  params: IChildParams,
  mutate: MutationFunction<UpsertChild, UpsertChildVariables>,
  onChangeValidationHints: (validationHints: IChildValidation) => void,
) {
  const validationHints = validate(params)
  if (validationHints) return onChangeValidationHints(validationHints)

  onChangeValidationHints({})

  const { birthday, desiredEnrollmentDate, firstName, gender, id, lastName } = params

  mutate({
    variables: {
      child: {
        birthday: formatDateInput(birthday),
        desiredEnrollmentDate: formatDateInput(desiredEnrollmentDate),
        firstName: firstName.trim(),
        gender: gender as Gender,
        id,
        lastName: lastName.trim(),
      },
    },
  })
}

function buttonCta(child: IChild | null, loading: boolean, t: TFunction) {
  if (child) {
    return loading ? t(`${PRE}-SAVING`) : t(`${PRE}-SAVE`)
  }

  return loading ? t(`${PRE}-ADDING`) : t(`${PRE}-ADD`)
}
